import React, { useState, useEffect } from 'react';
import { 
  Mail, 
  Eye, 
  MessageSquare, 
  Clock, 
  User, 
  Phone, 
  Building, 
  Tag,
  Filter,
  Search,
  X,
  Send,
  Paperclip,
  CheckCircle,
  AlertCircle,
  Archive
} from 'lucide-react';
import { contactsAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import {
  ContactContainer,
  SectionHeader,
  Title,
  Subtitle,
  ActionBar,
  FilterSection,
  Select,
  Input,
  ActionButton,
  ContactsGrid,
  ContactCard,
  ContactHeader,
  ContactInfo,
  ContactName,
  ContactEmail,
  ContactMeta,
  ContactStatus,
  ContactDate,
  ContactMessage,
  ContactActions,
  ViewButton,
  ReplyButton,
  StatusButton,
  Pagination,
  PageButton,
  LoadingSpinner,
  ErrorMessage,
  EmptyState,
  EmptyStateIcon,
  EmptyStateText,
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ContactDetail,
  ContactDetailHeader,
  ContactDetailInfo,
  ContactDetailMeta,
  ContactDetailMessage,
  RepliesSection,
  ReplyCard,
  ReplyHeader,
  ReplyContent,
  ReplyAttachments,
  ReplyForm,
  FormGroup,
  Label,
  TextArea,
  FileInput,
  FileLabel,
  SubmitButton,
  AttachmentPreview,
  AttachmentItem
} from './ContactSection.styles';

const ContactSection = () => {
  const { showSuccess, showError } = useToast();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [filters, setFilters] = useState({
    page: 1,
    per_page: 10,
    status: ''
  });
  const [replyForm, setReplyForm] = useState({
    subject: '',
    message: '',
    attachments: []
  });

  useEffect(() => {
    loadContacts();
  }, [filters]);

  const loadContacts = async () => {
    try {
      setLoading(true);
      const params = { ...filters };
      // Remove empty filter values
      Object.keys(params).forEach(key => {
        if (params[key] === '') {
          delete params[key];
        }
      });
      
      const response = await contactsAPI.getAll(params);
      if (response.status === 200) {
        setContacts(response.data.contacts || []);
        setPagination({
          total: response.data.total,
          page: response.data.page,
          per_page: response.data.per_page,
          pages: response.data.pages
        });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to load contacts';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const openContactDetail = async (contact) => {
    try {
      setLoading(true);
      const response = await contactsAPI.getById(contact.id);
      if (response.status === 200) {
        setSelectedContact(response.data);
        setReplyForm({
          subject: `Re: ${contact.service_type} Inquiry`,
          message: '',
          attachments: []
        });
        setShowModal(true);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to load contact details';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedContact(null);
    setReplyForm({
      subject: '',
      message: '',
      attachments: []
    });
  };

  const handleReplySubmit = async (e) => {
    e.preventDefault();
    
    if (!replyForm.subject.trim() || !replyForm.message.trim()) {
      setError('Please fill in subject and message');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const formData = new FormData();
      formData.append('subject', replyForm.subject);
      formData.append('message', replyForm.message);
      
      // Add attachments
      replyForm.attachments.forEach((file, index) => {
        formData.append('attachments', file);
      });

      const response = await contactsAPI.reply(selectedContact.id, formData);
      if (response.status === 201) {
        showSuccess('Reply sent successfully!');
        // Reload contact details to show the new reply
        await openContactDetail(selectedContact);
        // Reload contacts list
        loadContacts();
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to send reply';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (contactId, newStatus) => {
    try {
      setLoading(true);
      const response = await contactsAPI.updateStatus(contactId, newStatus);
      if (response.status === 200) {
        showSuccess('Contact status updated successfully!');
        loadContacts();
        // Update selected contact if it's the same one
        if (selectedContact && selectedContact.id === contactId) {
          setSelectedContact(prev => ({ ...prev, status: newStatus }));
        }
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update status';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleAttachmentChange = (e) => {
    const files = Array.from(e.target.files);
    setReplyForm(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...files]
    }));
  };

  const removeAttachment = (index) => {
    setReplyForm(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return '#3B82F6'; // Blue
      case 'replied': return '#10B981'; // Green
      case 'closed': return '#6B7280'; // Gray
      default: return '#6B7280';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'new': return <AlertCircle size={16} />;
      case 'replied': return <CheckCircle size={16} />;
      case 'closed': return <Archive size={16} />;
      default: return <AlertCircle size={16} />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <ContactContainer>
      <SectionHeader>
        <Title>Contact Management</Title>
        <Subtitle>Manage customer inquiries and support messages</Subtitle>
      </SectionHeader>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <ActionBar>
        <FilterSection>
          <Select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <option value="">All Status</option>
            <option value="new">New</option>
            <option value="replied">Replied</option>
            <option value="closed">Closed</option>
          </Select>
        </FilterSection>
      </ActionBar>

      {loading && contacts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '4rem' }}>
          <LoadingSpinner />
          <p style={{ color: '#64748B', marginTop: '1rem' }}>Loading contacts...</p>
        </div>
      ) : contacts.length === 0 ? (
        <EmptyState>
          <EmptyStateIcon>📧</EmptyStateIcon>
          <EmptyStateText>No contact messages found.</EmptyStateText>
        </EmptyState>
      ) : (
        <>
          <ContactsGrid>
            {contacts.map((contact) => (
              <ContactCard key={contact.id}>
                <ContactHeader>
                  <ContactInfo>
                    <ContactName>{contact.full_name}</ContactName>
                    <ContactEmail>{contact.email}</ContactEmail>
                  </ContactInfo>
                  <ContactStatus status={contact.status}>
                    {getStatusIcon(contact.status)}
                    {contact.status.charAt(0).toUpperCase() + contact.status.slice(1)}
                  </ContactStatus>
                </ContactHeader>
                
                <ContactMeta>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Tag size={14} />
                    <span>{contact.service_type}</span>
                  </div>
                  {contact.company && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Building size={14} />
                      <span>{contact.company}</span>
                    </div>
                  )}
                  {contact.phone_number && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Phone size={14} />
                      <span>{contact.phone_number}</span>
                    </div>
                  )}
                  <ContactDate>
                    <Clock size={14} />
                    {formatDate(contact.created_at)}
                  </ContactDate>
                </ContactMeta>

                <ContactMessage>
                  {contact.message.length > 150 
                    ? `${contact.message.substring(0, 150)}...` 
                    : contact.message
                  }
                </ContactMessage>

                <ContactActions>
                  <ViewButton onClick={() => openContactDetail(contact)}>
                    <Eye size={14} />
                    View Details
                  </ViewButton>
                  {contact.status === 'new' && (
                    <ReplyButton onClick={() => openContactDetail(contact)}>
                      <MessageSquare size={14} />
                      Reply
                    </ReplyButton>
                  )}
                </ContactActions>
              </ContactCard>
            ))}
          </ContactsGrid>

          {pagination.pages > 1 && (
            <Pagination>
              <PageButton
                disabled={filters.page === 1}
                onClick={() => handlePageChange(filters.page - 1)}
              >
                Previous
              </PageButton>
              
              {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                const page = i + 1;
                return (
                  <PageButton
                    key={page}
                    active={filters.page === page}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </PageButton>
                );
              })}
              
              <PageButton
                disabled={filters.page === pagination.pages}
                onClick={() => handlePageChange(filters.page + 1)}
              >
                Next
              </PageButton>
            </Pagination>
          )}
        </>
      )}

      {showModal && selectedContact && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Contact Details</ModalTitle>
              <CloseButton onClick={closeModal}>
                <X size={20} />
              </CloseButton>
            </ModalHeader>

            <ContactDetail>
              <ContactDetailHeader>
                <ContactDetailInfo>
                  <ContactName>{selectedContact.full_name}</ContactName>
                  <ContactEmail>{selectedContact.email}</ContactEmail>
                </ContactDetailInfo>
                <ContactStatus status={selectedContact.status}>
                  {getStatusIcon(selectedContact.status)}
                  {selectedContact.status.charAt(0).toUpperCase() + selectedContact.status.slice(1)}
                </ContactStatus>
              </ContactDetailHeader>

              <ContactDetailMeta>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Tag size={16} />
                  <span><strong>Service:</strong> {selectedContact.service_type}</span>
                </div>
                {selectedContact.company && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Building size={16} />
                    <span><strong>Company:</strong> {selectedContact.company}</span>
                  </div>
                )}
                {selectedContact.phone_number && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Phone size={16} />
                    <span><strong>Phone:</strong> {selectedContact.phone_number}</span>
                  </div>
                )}
                <ContactDate>
                  <Clock size={16} />
                  <strong>Received:</strong> {formatDate(selectedContact.created_at)}
                </ContactDate>
              </ContactDetailMeta>

              <ContactDetailMessage>
                <h4>Message:</h4>
                <p>{selectedContact.message}</p>
              </ContactDetailMessage>

              {selectedContact.replies && selectedContact.replies.length > 0 && (
                <RepliesSection>
                  <h4>Replies ({selectedContact.replies.length})</h4>
                  {selectedContact.replies.map((reply) => (
                    <ReplyCard key={reply.id}>
                      <ReplyHeader>
                        <div>
                          <strong>{reply.subject}</strong>
                          <span style={{ color: '#64748B', fontSize: '0.875rem' }}>
                            by {reply.admin_name} • {formatDate(reply.sent_at)}
                          </span>
                        </div>
                      </ReplyHeader>
                      <ReplyContent>
                        <p>{reply.message}</p>
                        {reply.attachments && reply.attachments.length > 0 && (
                          <ReplyAttachments>
                            <strong>Attachments:</strong>
                            {reply.attachments.map((attachment, index) => (
                              <div key={index} style={{ color: '#3B82F6', fontSize: '0.875rem' }}>
                                📎 {attachment}
                              </div>
                            ))}
                          </ReplyAttachments>
                        )}
                      </ReplyContent>
                    </ReplyCard>
                  ))}
                </RepliesSection>
              )}

              <ReplyForm onSubmit={handleReplySubmit}>
                <h4>Send Reply</h4>
                
                <FormGroup>
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    id="subject"
                    type="text"
                    value={replyForm.subject}
                    onChange={(e) => setReplyForm(prev => ({ ...prev, subject: e.target.value }))}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="message">Message *</Label>
                  <TextArea
                    id="message"
                    value={replyForm.message}
                    onChange={(e) => setReplyForm(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Type your reply here..."
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="attachments">Attachments</Label>
                  <FileInput
                    type="file"
                    id="attachments"
                    multiple
                    onChange={handleAttachmentChange}
                  />
                  <FileLabel htmlFor="attachments">
                    <Paperclip size={16} />
                    Add Attachments
                  </FileLabel>
                  
                  {replyForm.attachments.length > 0 && (
                    <AttachmentPreview>
                      {replyForm.attachments.map((file, index) => (
                        <AttachmentItem key={index}>
                          <span>{file.name}</span>
                          <button
                            type="button"
                            onClick={() => removeAttachment(index)}
                            style={{ background: 'none', border: 'none', color: '#DC2626', cursor: 'pointer' }}
                          >
                            <X size={14} />
                          </button>
                        </AttachmentItem>
                      ))}
                    </AttachmentPreview>
                  )}
                </FormGroup>

                <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                  <SubmitButton type="submit" disabled={loading}>
                    {loading ? (
                      <LoadingSpinner />
                    ) : (
                      <>
                        <Send size={16} />
                        Send Reply
                      </>
                    )}
                  </SubmitButton>
                  
                  {selectedContact.status !== 'closed' && (
                    <StatusButton
                      type="button"
                      onClick={() => handleStatusUpdate(selectedContact.id, 'closed')}
                    >
                      <Archive size={16} />
                      Close Contact
                    </StatusButton>
                  )}
                </div>
              </ReplyForm>
            </ContactDetail>
          </ModalContent>
        </Modal>
      )}
    </ContactContainer>
  );
};

export default ContactSection;
